import type { RendererProps } from "../core/element-registry"
import { ElementRenderer } from "./element-renderer"
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  SidebarInset,
} from "../components/ui/sidebar"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

/**
 * SidebarRenderer — renders a Cogo JSON element of type "sidebar" as a
 * shadcn/ui Sidebar layout with SidebarProvider + Sidebar + SidebarInset.
 *
 * NavigationData is NOT handled here — it is registered as #{nav.*} state
 * in cogo-app.tsx. UI design lives entirely in ui.json children.
 *
 * Children are categorized by their `role` property:
 *   - "sidebar-header"  → rendered inside SidebarHeader
 *   - "sidebar-content" → rendered inside SidebarContent
 *   - "sidebar-footer"  → rendered inside SidebarFooter
 *   - "main"            → rendered inside SidebarInset (the content area)
 *   - No role           → defaults to SidebarContent
 */
export function SidebarRenderer({ element }: RendererProps) {
  const raw = element as unknown as Record<string, unknown>
  const collapsible = raw.collapsible as
    | "icon"
    | "offcanvas"
    | "none"
    | undefined
  const side = raw.side as "left" | "right" | undefined
  const variant = (element.variant as "sidebar" | "floating" | "inset" | undefined) ?? "sidebar"
  const defaultOpen = raw.defaultOpen !== false

  const children = element.children ?? []

  // Categorize children by role
  const headerChildren = children.filter((c) => c.role === "sidebar-header")
  const contentChildren = children.filter(
    (c) => !c.role || c.role === "sidebar-content",
  )
  const footerChildren = children.filter((c) => c.role === "sidebar-footer")
  const mainChildren = children.filter((c) => c.role === "main")

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <Sidebar
        collapsible={collapsible ?? "icon"}
        side={side ?? "left"}
        variant={variant}
        className={cn(stripPresetConflicts(element.className))}
        data-testid={`sidebar-${element.id}`}
      >
        {headerChildren.length > 0 && (
          <SidebarHeader>
            {headerChildren.map((child, index) => (
              <ElementRenderer key={`header-${index}-${child.id}`} element={child} />
            ))}
          </SidebarHeader>
        )}
        {contentChildren.length > 0 && (
          <SidebarContent>
            {contentChildren.map((child, index) => (
              <ElementRenderer key={`content-${index}-${child.id}`} element={child} />
            ))}
          </SidebarContent>
        )}
        {footerChildren.length > 0 && (
          <SidebarFooter>
            {footerChildren.map((child, index) => (
              <ElementRenderer key={`footer-${index}-${child.id}`} element={child} />
            ))}
          </SidebarFooter>
        )}
        <SidebarRail />
      </Sidebar>
      {mainChildren.length > 0 ? (
        <SidebarInset>
          {mainChildren.map((child, index) => (
            <ElementRenderer key={`main-${index}-${child.id}`} element={child} />
          ))}
        </SidebarInset>
      ) : null}
    </SidebarProvider>
  )
}

/**
 * SidebarInsetRenderer — renders the content area wrapper when using sidebar inset variant.
 * Can be used standalone when the SidebarProvider is provided by a parent layout.
 */
export function SidebarInsetRenderer({ element }: RendererProps) {
  const children = element.children ?? []

  return (
    <SidebarInset className={cn(element.className)}>
      {children.map((child, index) => (
        <ElementRenderer key={`inset-${index}-${child.id}`} element={child} />
      ))}
    </SidebarInset>
  )
}

/**
 * SidebarTriggerRenderer — renders the sidebar toggle button.
 */
export function SidebarTriggerRenderer({ element }: RendererProps) {
  return <SidebarTrigger className={cn(element.className)} />
}
